import { TIMELINE_CENTER_FROM_BOTTOM_PX, barHeightPx } from './scale';
import type { TimelineScrubberBucket } from './types';

export interface TooltipConnectorProps {
  bucket: TimelineScrubberBucket;
  /** Pre-formatted "HH:00 UTC"/"HH:00 <tz>" readout for this bucket. */
  clockLabel: string;
  /** Horizontal center of the bucket, as a percentage of the track width. */
  leftPct: number;
  /** `[0, 1]`, already computed via `normalizeHeight` for this bucket. */
  heightRatio: number;
}

/**
 * Tooltip + dashed lead-line for whichever bucket is currently previewed or
 * selected. Rendered as a single persistent instance — `TimelineScrubber`
 * keeps this mounted for as long as any bucket is displayed, only ever
 * changing `leftPct`/`heightRatio`/content, so moving between adjacent
 * buckets slides (via the `transition-all` below) instead of flickering out
 * and back in.
 *
 * The column's height stops exactly at the bucket's *top* — not its center,
 * and not the track's edge — so the guide line always lands on the bar it
 * points at, however short that bar is.
 */
export function TooltipConnector({
  bucket,
  clockLabel,
  leftPct,
  heightRatio,
}: TooltipConnectorProps) {
  const barTopFromBottomPx = TIMELINE_CENTER_FROM_BOTTOM_PX + barHeightPx(heightRatio) / 2;

  return (
    <div
      className="pointer-events-none absolute top-0 flex -translate-x-1/2 flex-col items-center transition-all duration-150 ease-out"
      style={{
        left: `${leftPct}%`,
        height: `calc(100% - ${barTopFromBottomPx}px)`,
      }}
    >
      {/* Guide line: spans the column's whole height — starting behind the
          tooltip and running down to the top edge of the bar — rather than
          starting where the tooltip ends. Stacked below the tooltip (z-0 vs
          z-10) so the label always reads cleanly over it. */}
      <div className="absolute inset-y-0 left-1/2 z-0 w-px -translate-x-1/2 border-l border-dashed border-(--scrubber-accent)/60" />
      <div className="relative z-10 flex flex-col items-center gap-0.5 rounded-lg bg-(--scrubber-accent) px-3 py-1.5 text-center shadow-(--shadow-popover)">
        <span className="whitespace-nowrap text-[0.846em] font-bold leading-none text-(--scrubber-on-accent)">
          {bucket.count} external OI{bucket.count === 1 ? '' : 's'}
        </span>
        <span className="whitespace-nowrap text-[0.615em] font-medium leading-none text-(--scrubber-on-accent)/70">
          T+{bucket.offsetHours}:00 · {clockLabel}
        </span>
      </div>
    </div>
  );
}
